import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5 } from "../commonApis/commonApis";
const url = process.env.REACT_APP_BASE_URL;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

export const useDRSCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(`${url}/api/drs`, payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res;
    },
    onSuccess: (res: any) => {
      console.log("✅ DRS Created successfully:", res);
      queryClient.invalidateQueries({ queryKey: ["DRSData"] });
      queryClient.invalidateQueries({ queryKey: ["routeOrdersData"] });
      return res;
    },
    onError: (error: any) => {
      console.error("❌ DRS create request failed:", error.message);
    },
  });
};

export const useDRSCreateByAWBMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(`${url}/api/drs/by-awb`, payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res;
    },
    onSuccess: (res: any) => {
      console.log("✅ DRS Created successfully:", res);
      queryClient.invalidateQueries({ queryKey: ["DRSData"] });
      queryClient.invalidateQueries({ queryKey: ["routeOrdersData"] });
      return res;
    },
    onError: (error: any) => {
      console.error("❌ DRS create request failed:", error.message);
    },
  });
};

export const useDRSShipmentUpdateByAWBMutation = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/drs/by-awb/${id}`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res;
    },
    onSuccess: (res: any) => {
      console.log("✅ DRS Created successfully:", res);
      queryClient.invalidateQueries({ queryKey: ["DRSData"] });
      queryClient.invalidateQueries({ queryKey: ["DRSDetails", id] });
      queryClient.invalidateQueries({ queryKey: ["routeOrdersData"] });
      return res;
    },
    onError: (error: any) => {
      console.error("❌ DRS create request failed:", error.message);
      return error;
    },
  });
};

export const useFetchDRSData = (
  page: number,
  limit: number,
  searchFor: string,
) => {
  return useQuery({
    queryKey: ["DRSData", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/drs?page=${page}&limit=${limit}&searchFor=${searchFor}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchCodOrdersTransactionsData = (
  page: number,
  limit: number,
  searchFor: string,
) => {
  return useQuery({
    queryKey: ["CodOrdersTransactionsData", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/drs/cod-orders/transactions?page=${page}&limit=${limit}&searchFor=${searchFor}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 0,
  });
};

export const useFetchDRSDetailsData = (drsId?: string, filter?: string) => {
  return useQuery({
    queryKey: ["DRSDetails", drsId, filter],
    queryFn: async () => {
      if (!drsId) return null; // 👈 early exit inside the hook logic
      const res = await fetch3(
        `${url}/api/drs/${drsId}?type=LM&orderType=${filter}`,
        "get",
      );

      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!drsId, // 👈 run only when id exists
    staleTime: 1000 * 60 * 5,
  });
};

export const useDRSUpdateMutation = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/drs/${id}`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ DRS updated successfully:", data);
      queryClient.invalidateQueries({ queryKey: ["DRSData"] });
      queryClient.invalidateQueries({ queryKey: ["DRSDetails", id] });
      queryClient.invalidateQueries({ queryKey: ["routeOrdersData"] });
    },
    onError: (error: any) => {
      console.error("❌ DRS update request failed:", error.message);
    },
  });
};

// /shipments/return
export const useDRShipmentReturnMutation = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/drs/${id}/shipments/return`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ DRS shipment returned successfully:", data);
      queryClient.invalidateQueries({ queryKey: ["DRSData"] });
      queryClient.invalidateQueries({ queryKey: ["DRSDetails", id] });
    },
    onError: (error: any) => {
      console.error("❌ DRS shipment return request failed:", error.message);
    },
  });
};

export const useDRSShipmentUpdateMutation = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/drs/${id}/shipment`,
        "PUT",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || res?.error || "Something went wrong";
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log("✅ DRS updated successfully:", data);
      queryClient.invalidateQueries({ queryKey: ["DRSData"] });
      queryClient.invalidateQueries({ queryKey: ["DRSDetails", id] });
    },
    onError: (error: any) => {
      console.error("❌ DRS update request failed:", error.message);
      return error;
    },
  });
};

// /shipments/payment-collect
export const useDRSShipmentPaymentCollectMutation = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/drs/${id}/shipments/payment-collect`,
        "PUT",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || res?.error || "Something went wrong";
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log("✅ DRS updated successfully:", data);
      queryClient.invalidateQueries({ queryKey: ["CodOrdersTransactionsData"] });
      queryClient.invalidateQueries({ queryKey: ["DRSData"] });
    },
    onError: (error: any) => {
      console.error("❌ DRS update request failed:", error.message);
      return error;
    },
  });
};

export const useDRSDownloadMutation = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const res = await fetch(`${url}/api/drs/${id}/download`, {
          method: "GET",
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error(res.statusText || "Failed to download DRS");
        }

        const blob = await res.blob();
        const pdfUrl = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = `${id}.pdf`;
        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(pdfUrl);
        document.body.removeChild(a);

        return blob;
      } catch (error) {
        console.error("Download failed:", error);
        throw error;
      }
    },
    onSuccess: () => {
      console.log("✅ DRS downloaded successfully");
    },
    onError: (error) => {
      console.error("❌ DRS download failed:", error.message);
    },
  });
};

export const useDRSDeliveredOrderDownloadMutation = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const res = await fetch(
          `${url}/api/drs/${id}/download/delivered-orders`,
          {
            method: "GET",
            credentials: "include",
          },
        );

        if (!res.ok) {
          throw new Error(res.statusText || "Failed to download DRS");
        }

        const blob = await res.blob();
        const pdfUrl = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = `${id}.pdf`;
        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(pdfUrl);
        document.body.removeChild(a);

        return blob;
      } catch (error) {
        console.error("Download failed:", error);
        throw error;
      }
    },
    onSuccess: () => {
      console.log("✅ DRS downloaded successfully");
    },
    onError: (error) => {
      console.error("❌ DRS download failed:", error.message);
    },
  });
};

// Revert DRS Shipment Status
export const useDRSShipmentRevertStatusMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/drs/status/revert`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ DRS shipment status reverted successfully:", data);
      queryClient.invalidateQueries({ queryKey: ["DRSData"] });
    },
    onError: (error: any) => {
      console.error(
        "❌ DRS shipment revert status request failed:",
        error.message,
      );
    },
  });
};
